import React, { useEffect, useState, useCallback } from 'react';
import { posts as postsApi } from '../../lib/api';
import { getIntlLocale } from '../../i18n/runtime';
import {
  Loader2,
  Heart,
  MessageSquare,
  Eye,
  ExternalLink,
  Sparkles,
  Check,
  X,
  Edit3,
} from 'lucide-react';

export default function Posts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postsApi.list();
      setItems(data.posts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decideDraft = async (id, decision, edited) => {
    setBusyId(id);
    try {
      await postsApi.decideDraft(id, decision, edited);
      await load();
      setEditingId(null);
      setEditText('');
    } catch (err) {
      alert(err.message || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const formatNum = (n) => {
    if (!n) return '0';
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  return (
    <div className="app-page-shell bg-white" data-testid="posts-page">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center px-6 sticky top-0 bg-white z-10">
        <h1 className="text-lg font-semibold text-[#191919]">Posts</h1>
        <span className="ml-3 text-xs text-[#6B6B6B]">{items.length} total</span>
      </header>

      <div className="app-page-scroll">
        <div className="max-w-3xl mx-auto p-6 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B6B6B]" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-[#FBFBFA] border border-[#E3E2E0] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-[#6B6B6B]" />
            </div>
            <h3 className="text-base font-medium text-[#191919]">No posts yet</h3>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Posts authored by your connected LinkedIn account will appear here, plus any AI-drafted posts awaiting your approval.
            </p>
          </div>
        ) : (
          items.map((p) => (
            <article
              key={p.id}
              className={`border rounded-xl bg-white p-4 ${
                p.needs_approval ? 'border-[#EADDFF] bg-[#FBF7FF]' : 'border-[#E3E2E0]'
              }`}
              data-testid={`post-${p.id}`}
            >
              <header className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
                  {p.needs_approval ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#EADDFF] text-[#5A3EA8] font-medium">
                      <Sparkles className="w-3 h-3" /> Pending approval
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#E6F4EA] text-[#1E7F4E] font-medium">
                      Published
                    </span>
                  )}
                  <span>{fmtDate(p.created_at)}</span>
                </div>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#0052CC] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" /> Open on LinkedIn
                  </a>
                )}
              </header>

              {editingId === p.id ? (
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={5}
                  className="w-full bg-white border border-[#E3E2E0] rounded-md text-sm p-3 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
                  data-testid={`post-edit-${p.id}`}
                />
              ) : (
                <p className="text-sm text-[#191919] whitespace-pre-wrap leading-relaxed">{p.text}</p>
              )}

              {p.image_url && !editingId && (
                <img src={p.image_url} alt="" className="mt-3 rounded-lg border border-[#E3E2E0] max-h-64 object-cover" />
              )}

              {!p.needs_approval ? (
                <div className="mt-4 flex items-center gap-5 text-xs text-[#6B6B6B]" data-testid={`post-stats-${p.id}`}>
                  <Metric icon={Heart} label="likes" value={p.likes} available={p.metrics_available?.likes} formatNum={formatNum} />
                  <Metric icon={MessageSquare} label="comments" value={p.comments} available={p.metrics_available?.comments} formatNum={formatNum} />
                  <Metric icon={Eye} label="views" value={p.views} available={p.metrics_available?.views} formatNum={formatNum} />
                </div>
              ) : (
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => decideDraft(p.id, 'approve', editingId === p.id ? editText : null)}
                    disabled={busyId === p.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1E7F4E] hover:bg-[#176A40] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                    data-testid={`post-approve-${p.id}`}
                  >
                    {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {editingId === p.id ? 'Approve edited' : 'Approve & publish'}
                  </button>
                  <button
                    onClick={() => {
                      if (editingId === p.id) {
                        setEditingId(null);
                        setEditText('');
                      } else {
                        setEditingId(p.id);
                        setEditText(p.text || '');
                      }
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E3E2E0] text-sm rounded-lg hover:bg-[#F0F0EF]"
                    data-testid={`post-edit-btn-${p.id}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    {editingId === p.id ? 'Cancel edit' : 'Customize'}
                  </button>
                  <button
                    onClick={() => decideDraft(p.id, 'reject')}
                    disabled={busyId === p.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#CE3636] hover:bg-[#FFEBE9] text-sm rounded-lg ml-auto"
                    data-testid={`post-reject-${p.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                    Deny
                  </button>
                </div>
              )}
            </article>
          ))
        )}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, available, formatNum }) {
  // `available` undefined → backwards compatible (assume value is real).
  const isMissing = available === false;
  return (
    <span
      className="inline-flex items-center gap-1"
      title={isMissing ? `${label} not provided by LinkedIn for this account` : undefined}
    >
      <Icon className="w-3.5 h-3.5" /> {isMissing ? '—' : formatNum(value)} {label}
    </span>
  );
}
